import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    LogOut, Clock,
    Calendar, Hash,
    Building2, UserCheck, CalendarDays,
    FileText, Eye,
} from 'lucide-react';

import InboxHeader from '../../components/Inbox/InboxHeader';
import StatsCards from '../../components/Inbox/StatsCards';
import ActionButtons from '../../components/Inbox/ActionButtons';
import RemarksHistory from '../../components/Inbox/RemarksHistory';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import VerificationInput from '../../components/Inbox/VerificationInput';
import AttachmentModal from '../../components/Inbox/AttachmentModal';

import {
    fetchVerifyEmpExit,
    fetchEmpExitById,
    approveEmpExit,
    setSelectedId,
    clearApproveResult,
    clearExitDetail,
    resetAll,
    selectExitGridListArray,
    selectExitViewData,
    selectExitGridListLoading,
    selectExitViewDataLoading,
    selectApproveLoading,
    selectExitGridListError,
    selectExitViewDataError,
    selectApproveStatus,
    selectExitSummary,
} from '../../slices/HRSlice/employeeExitSlice';

import {
    fetchRemarks,
    selectRemarks,
    selectRemarksLoading,
    setSelectedMOID
} from '../../slices/supplierPOSlice/purcahseHelperSlice';

import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton
} from '../../slices/CommonSlice/getStatusSlice';

const VerifyEmployeeExit = ({ notificationData, onNavigate }) => {
    const dispatch = useDispatch();

    // ── Selectors ─────────────────────────────────────────────────────────────
    const exitGrid        = useSelector(selectExitGridListArray);
    const exitViewRaw     = useSelector(selectExitViewData);
    const inboxLoading    = useSelector(selectExitGridListLoading);
    const inboxError      = useSelector(selectExitGridListError);
    const detailsLoading  = useSelector(selectExitViewDataLoading);
    const detailsError    = useSelector(selectExitViewDataError);
    const approvalLoading = useSelector(selectApproveLoading);
    const approvalStatus  = useSelector(selectApproveStatus);
    const summary         = useSelector(selectExitSummary);
    const remarks         = useSelector(selectRemarks);
    const remarksLoading  = useSelector(selectRemarksLoading);
    const statusLoading   = useSelector(selectStatusListLoading);
    const statusError     = useSelector(selectStatusListError);
    const enabledActions  = useSelector(selectEnabledActions);
    const hasActions      = useSelector(selectHasActions);

    const { userData, userDetails } = useSelector((state) => state.auth);
    const roleId = userData?.roleId || userData?.RID;

    // Unwrap Data[0] if slice stored the array
    const exitView = Array.isArray(exitViewRaw)
        ? (exitViewRaw[0] || null)
        : exitViewRaw;

    // ── Local State ────────────────────────────────────────────────────────────
    const [selectedItem,         setSelectedItem]         = useState(null);
    const [isVerified,           setIsVerified]           = useState(false);
    const [verificationComment,  setVerificationComment]  = useState('');
    const [showRemarksHistory,   setShowRemarksHistory]   = useState(false);
    const [searchQuery,          setSearchQuery]          = useState('');
    const [filterStatus,         setFilterStatus]         = useState('All');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered,   setIsLeftPanelHovered]   = useState(false);
    const [showDocumentModal,    setShowDocumentModal]    = useState(false);

    const { InboxTitle, ModuleDisplayName } = notificationData || {};

    // "d" = unified display: detail when loaded, else inbox item
    const d = exitView || selectedItem || {};

    // ── Helpers ────────────────────────────────────────────────────────────────
    const getCurrentUser = () =>
        userData?.userName || userDetails?.userName || 'system';

    // Build a Blob URL from the raw DocBaseString (base64, no data: prefix)
    const getDocumentUrl = (docBaseString, docType) => {
        if (!docBaseString) return null;
        try {
            const byteCharacters = atob(docBaseString);
            const byteArray = new Uint8Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) byteArray[i] = byteCharacters.charCodeAt(i);
            const ext = (docType || '').toLowerCase();
            let mimeType = 'application/octet-stream';
            if (ext === 'pdf') mimeType = 'application/pdf';
            else if (['jpg', 'jpeg'].includes(ext)) mimeType = 'image/jpeg';
            else if (ext === 'png') mimeType = 'image/png';
            return URL.createObjectURL(new Blob([byteArray], { type: mimeType }));
        } catch (error) {
            console.error('Error creating document URL:', error);
            return null;
        }
    };

    const getCurrentRoleName = () =>
        userDetails?.roleName        ||
        userData?.roleName           ||
        notificationData?.InboxTitle ||
        notificationData?.ModuleDisplayName ||
        'Exit Verifier';

    const updateRemarksHistory = (existing, roleName, userName, comment) => {
        const formatted = `${roleName} : ${userName} : ${comment}`;
        if (!existing || existing.trim() === '') return formatted;
        return `${existing.trim()}||${formatted}`;
    };

    const uniqueStatuses = [...new Set(exitGrid.map(i => i.Status))].filter(Boolean);

    // ── Init ───────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (roleId) {
            console.log('🎯 Initializing Employee Exit Verification — RoleID:', roleId);
            dispatch(fetchVerifyEmpExit(roleId));
        }
    }, [roleId, dispatch]);

    useEffect(() => {
        dispatch(setShowReturnButton('Yes'));
        return () => {
            dispatch(resetAll());
            dispatch(resetApprovalData());
            dispatch(clearApproveResult());
        };
    }, [dispatch]);

    // ── Fetch detail view when item is selected ───────────────────────────────
    useEffect(() => {
        if (selectedItem?.Id) {
            console.log('🔍 Fetching Exit View — Id:', selectedItem.Id);
            dispatch(setSelectedId(selectedItem.Id));
            dispatch(fetchEmpExitById({
                empRefNo: selectedItem.EmpRefNo,
                id:       selectedItem.Id,
                roleId,
            }));
            setIsVerified(false);
            setVerificationComment('');
            setShowRemarksHistory(false);
        }
    }, [selectedItem, dispatch]);

    // ── Fetch status list after detail loads ──────────────────────────────────
    useEffect(() => {
        if (selectedItem && roleId && exitView?.MOID) {
            const moid = Number(exitView.MOID);
            console.log('📊 Fetching Status List — MOID:', moid, 'ROID:', roleId);
            dispatch(fetchStatusList({ MOID: moid, ROID: roleId, ChkAmt: 0 }));
        }
    }, [selectedItem, roleId, exitView?.MOID, dispatch]);

    // ── Fetch remarks ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (selectedItem && exitView?.MOID) {
            const moid = Number(exitView.MOID);
            const trno = String(exitView.Id || selectedItem.Id || '');
            console.log('💬 Fetching Remarks — trno:', trno, 'MOID:', moid);
            dispatch(setSelectedMOID(moid));
            dispatch(fetchRemarks({ trno, moid }));
        }
    }, [selectedItem, exitView?.MOID, dispatch]);

    // ── Auto-collapse left panel ──────────────────────────────────────────────
    useEffect(() => {
        if (selectedItem) setIsLeftPanelCollapsed(true);
    }, [selectedItem]);

    // ── Event handlers ────────────────────────────────────────────────────────
    const handleBackToInbox = () => {
        if (onNavigate) onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
    };

    const handleRefresh = () => {
        if (roleId) {
            dispatch(fetchVerifyEmpExit(roleId));
            if (selectedItem?.Id) {
                dispatch(fetchEmpExitById({
                    empRefNo: selectedItem.EmpRefNo,
                    id:       selectedItem.Id,
                    roleId,
                }));
            }
        }
    };

    const handleItemSelect = (item) => {
        console.log('✅ Selected Exit Item:', item);
        setSelectedItem(item);
    };

    const buildApprovalPayload = (actionValue) => {
        const currentUser     = getCurrentUser();
        const currentRoleName = getCurrentRoleName();
        const updatedRemarks  = updateRemarksHistory(
            exitView?.Remarks || '',
            currentRoleName, currentUser, verificationComment.trim()
        );
        const payload = {
            id:              exitView?.Id              || selectedItem?.Id              || '',
            costCenter:      exitView?.CostCenter      || exitView?.CCCode              || '',
            empRefNo:        exitView?.EmpRefNo        || selectedItem?.EmpRefNo        || '',
            groupId:         exitView?.GroupId         || selectedItem?.GroupId         || 0,
            resignationDate: exitView?.ResignationDate || selectedItem?.ResignationDate || '',
            relievingDate:   exitView?.RelievingDate   || selectedItem?.RelievingDate   || '',
            RoleId:          roleId,
            CreatedBy:       currentUser,
            Action:          actionValue,
            Note:            verificationComment.trim(),
            Remarks:         updatedRemarks,
        };
        console.log('📤 Employee Exit Approval Payload:', payload);
        return payload;
    };

    const handleActionClick = async (action) => {
        if (!selectedItem) { toast.error('No exit record selected'); return; }
        if (!verificationComment?.trim()) {
            toast.error('Verification comment is mandatory.');
            return;
        }
        if (!isVerified) {
            toast.error('Please check the verification checkbox before proceeding.');
            return;
        }

        let actionValue = action.value || action.text || action.type;
        if (!actionValue?.trim()) {
            const map = { approve: 'Approve', verify: 'Verify', reject: 'Reject', return: 'Return' };
            actionValue = map[action.type?.toLowerCase()] || 'Verify';
        }

        try {
            const payload = buildApprovalPayload(actionValue);
            const result  = await dispatch(approveEmpExit(payload)).unwrap();

            // Only Data === "Submited" is a real success — anything else is a business-rule error
            const dataVal = result?.Data;
            const dataStr = typeof dataVal === 'string' ? dataVal : (result?.Message || '');
            const isRealSuccess = dataStr.toLowerCase() === 'submited';

            if (!isRealSuccess) {
                throw new Error(dataStr || `Failed to ${actionValue}`);
            }

            toast.success(`${action.text || actionValue} completed successfully!`);

            setTimeout(() => {
                dispatch(fetchVerifyEmpExit(roleId));
                setSelectedItem(null);
                setVerificationComment('');
                setIsVerified(false);
                setShowRemarksHistory(false);
                setIsLeftPanelCollapsed(false);
                dispatch(clearExitDetail());
                dispatch(resetApprovalData());
                dispatch(clearApproveResult());
            }, 1000);

        } catch (error) {
            console.error('❌ Approval Error:', error);
            const msg =
                (typeof error === 'string' ? error : null) ||
                error?.message ||
                `Failed to ${action.text?.toLowerCase() || actionValue.toLowerCase()}`;
            toast.error(msg, { autoClose: 10000 });
        }
    };

    // ── Filtered list ─────────────────────────────────────────────────────────
    const filteredItems = exitGrid.filter(item => {
        const q = searchQuery.toLowerCase();
        const matchesSearch = !q ||
            item.EmployeeName?.toLowerCase().includes(q) ||
            item.EmpRefNo?.toLowerCase().includes(q)     ||
            String(item.Id).includes(q);
        const matchesStatus = filterStatus === 'All' || item.Status === filterStatus;
        return matchesSearch && matchesStatus;
    });

    // ── Stats cards ────────────────────────────────────────────────────────────
    const statsCards = [
        { icon: LogOut,       value: exitGrid.length,                   label: 'Total Records',        color: 'blue'   },
        { icon: Clock,        value: summary.pending || exitGrid.length, label: 'Pending Verification', color: 'purple' },
        { icon: CalendarDays, value: d.ResignationDate || '-',           label: 'Resignation Date',     color: 'indigo' },
        { icon: CalendarDays, value: d.RelievingDate   || '-',           label: 'Relieving Date',       color: 'violet' },
    ];

    // ── Left panel renderers ───────────────────────────────────────────────────
    const renderItemCard = (item) => (
        <div className="p-4">
            <div className="flex items-center space-x-3 mb-3">
                <div className="relative">
                    <div className="w-12 h-12 rounded-full border-2 border-blue-200 dark:border-blue-600 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-800/50 dark:to-purple-800/50 flex items-center justify-center">
                        <LogOut className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-purple-500 rounded-full border-2 border-white dark:border-gray-800" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">
                        {item.EmployeeName}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {item.EmpRefNo}
                    </p>
                </div>
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <div className="flex items-center justify-between">
                    <span className="flex items-center space-x-1">
                        <Hash className="w-3 h-3" />
                        <span>Ref: {item.Id}</span>
                    </span>
                    <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs font-medium">
                        Exit
                    </span>
                </div>
                {item.CCName && (
                    <div className="flex items-center space-x-1">
                        <Building2 className="w-3 h-3 text-blue-400" />
                        <span className="truncate">{item.CCName}</span>
                    </div>
                )}
                <div className="flex items-center space-x-1">
                    <Calendar className="w-3 h-3 text-indigo-400" />
                    <span>Resignation: {item.ResignationDate}</span>
                </div>
                <div className="flex items-center space-x-1">
                    <Calendar className="w-3 h-3 text-purple-400" />
                    <span>Relieving: {item.RelievingDate || '—'}</span>
                </div>
            </div>
        </div>
    );

    // Compact single-line row for the "classic" list view — same fields as
    // renderItemCard, laid out horizontally instead of stacked.
    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="font-semibold text-gray-900 dark:text-white min-w-[160px] truncate">
                {item.EmployeeName}
            </span>
            <span className="text-gray-500 dark:text-gray-400 min-w-[90px]">
                {item.EmpRefNo}
            </span>
            <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400 min-w-[100px]">
                <Hash className="w-3 h-3" />
                Ref: {item.Id}
            </span>
            {item.CCName && (
                <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400 min-w-[120px] truncate">
                    <Building2 className="w-3 h-3 text-blue-400" />
                    {item.CCName}
                </span>
            )}
            <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400 min-w-[140px]">
                <Calendar className="w-3 h-3 text-indigo-400" />
                {item.ResignationDate}
            </span>
            <span className="ml-auto px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs font-medium whitespace-nowrap">
                Exit
            </span>
        </div>
    );

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-blue-200 dark:border-blue-600 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-800/50 dark:to-purple-800/50 flex items-center justify-center">
            <LogOut className="w-4 h-4 text-blue-600 dark:text-blue-400" />
        </div>
    );

    // ── Exit Info Card ─────────────────────────────────────────────────────────
    const renderExitInfo = () => (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-700 to-purple-700 px-4 py-3">
                <h3 className="text-xs font-semibold text-white uppercase tracking-wider flex items-center space-x-2">
                    <LogOut className="w-4 h-4" />
                    <span>Exit Information</span>
                </h3>
            </div>
            <div className="p-6 bg-white dark:bg-gray-800">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-700">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 mb-3 shadow">
                            <UserCheck className="w-5 h-5 text-white" />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Employee</p>
                        <p className="font-bold text-blue-700 dark:text-blue-300 text-sm">
                            {d.EmployeeName || d.EmpName || 'N/A'}
                        </p>
                        <p className="text-xs text-indigo-500 dark:text-indigo-400 mt-0.5 font-medium">{d.EmpRefNo}</p>
                        {d.DesignationName && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{d.DesignationName}</p>
                        )}
                    </div>

                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 border border-purple-200 dark:border-purple-700">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 mb-3 shadow">
                            <Building2 className="w-5 h-5 text-white" />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Cost Center</p>
                        <p className="font-bold text-purple-700 dark:text-purple-300 text-sm">
                            {d.CostCenter || d.CCCode || 'N/A'}
                        </p>
                        {d.CCName && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{d.CCName}</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    // ── Timeline ───────────────────────────────────────────────────────────────
    const renderTimeline = () => (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-700 to-purple-700 px-4 py-3">
                <h3 className="text-xs font-semibold text-white uppercase tracking-wider">
                    Exit Details
                </h3>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
                <div className="flex items-center justify-between px-5 py-3">
                    <span className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        <Calendar className="w-4 h-4 text-indigo-400" />
                        <span>Resignation Date</span>
                    </span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        {d.ResignationDate || 'N/A'}
                    </span>
                </div>
                <div className="flex items-center justify-between px-5 py-3">
                    <span className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        <Calendar className="w-4 h-4 text-purple-400" />
                        <span>Relieving Date</span>
                    </span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        {d.RelievingDate || 'N/A'}
                    </span>
                </div>
                {d.DesignationName && (
                    <div className="flex items-center justify-between px-5 py-3">
                        <span className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            <UserCheck className="w-4 h-4 text-blue-400" />
                            <span>Designation</span>
                        </span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                            {d.DesignationName}
                        </span>
                    </div>
                )}
                {(d.NoticePereiodDays !== undefined && d.NoticePereiodDays !== null) && (
                    <div className="flex items-center justify-between px-5 py-3">
                        <span className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            <CalendarDays className="w-4 h-4 text-violet-400" />
                            <span>Notice Period (Days)</span>
                        </span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                            {d.NoticePereiodDays}
                        </span>
                    </div>
                )}
                {d.DocType && (
                    <div className="flex items-center justify-between px-5 py-3">
                        <span className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            <FileText className="w-4 h-4 text-blue-400" />
                            <span>Document Type</span>
                        </span>
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-semibold text-gray-900 dark:text-white uppercase">
                                {d.DocType}
                            </span>
                            {d.DocBaseString && (
                                <button
                                    onClick={() => setShowDocumentModal(true)}
                                    className="flex items-center gap-1 text-xs font-semibold text-purple-600 hover:text-purple-800 dark:text-purple-400 px-2 py-1 rounded hover:bg-purple-100 dark:hover:bg-purple-900/20 transition-colors"
                                    title="View Document"
                                >
                                    <Eye className="w-3.5 h-3.5" /> View
                                </button>
                            )}
                        </div>
                    </div>
                )}
                {d.Remarks?.trim() && (
                    <div className="flex flex-col px-5 py-3 gap-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Remarks</span>
                        <span className="text-sm text-gray-900 dark:text-white leading-relaxed">{d.Remarks}</span>
                    </div>
                )}
                {d.ReturnNotes?.trim() && (
                    <div className="flex flex-col px-5 py-3 gap-1 bg-yellow-50 dark:bg-yellow-900/10">
                        <span className="text-xs text-yellow-600 dark:text-yellow-400 uppercase tracking-wide font-semibold">Return Notes</span>
                        <span className="text-sm text-yellow-800 dark:text-yellow-300 leading-relaxed">{d.ReturnNotes}</span>
                    </div>
                )}
                {d.ErrorStatus?.trim() && (
                    <div className="flex flex-col px-5 py-3 gap-1 bg-red-50 dark:bg-red-900/10">
                        <span className="text-xs text-red-500 uppercase tracking-wide font-semibold">Error Status</span>
                        <span className="text-sm text-red-700 leading-relaxed">{d.ErrorStatus}</span>
                    </div>
                )}
            </div>
        </div>
    );

    // ── Detail pane ────────────────────────────────────────────────────────────
    const renderDetailContent = () => {
        if (!selectedItem) return null;
        const hasDetailData = !!exitView;

        return (
            <div className="space-y-6">

                {/* Loading indicator */}
                {detailsLoading && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-700">
                        <div className="flex items-center space-x-3">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                            <span className="text-blue-700 dark:text-blue-400 text-sm">Loading exit details...</span>
                        </div>
                    </div>
                )}

                {/* Hero header */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-2xl p-6 border-2 border-blue-200 dark:border-blue-700">
                    <div className="flex items-start space-x-4">
                        <div className="relative">
                            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                                <LogOut className="w-8 h-8 text-white" />
                            </div>
                            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-purple-500 rounded-full border-2 border-white dark:border-gray-800 flex items-center justify-center">
                                <UserCheck className="w-3 h-3 text-white" />
                            </div>
                        </div>

                        <div className="flex-1">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                                {d.EmployeeName || d.EmpName}
                            </h2>
                            <p className="text-indigo-600 dark:text-indigo-400 font-semibold mb-3">
                                {d.EmpRefNo} • Ref: {d.Id}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium">
                                    Employee Exit
                                </span>
                                {hasDetailData && d.MOID && (
                                    <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs font-medium">
                                        MOID: {d.MOID}
                                    </span>
                                )}
                                {d.DesignationName && (
                                    <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                                        {d.DesignationName}
                                    </span>
                                )}
                                {d.Status && (
                                    <span className="px-3 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded-full text-xs font-medium">
                                        Status: {d.Status}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Quick info chips */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-blue-200 dark:border-blue-700">
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Reference ID</p>
                            <p className="text-sm font-bold text-gray-900 dark:text-white">{d.Id || '-'}</p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Employee Code</p>
                            <p className="text-sm font-bold text-gray-900 dark:text-white">{d.EmpRefNo || '-'}</p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Resignation Date</p>
                            <p className="text-sm font-bold text-indigo-700 dark:text-indigo-300">{d.ResignationDate || '-'}</p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Relieving Date</p>
                            <p className="text-sm font-bold text-purple-700 dark:text-purple-300">{d.RelievingDate || '-'}</p>
                        </div>
                    </div>
                </div>

                {/* Exit info — gated on hasDetailData */}
                {hasDetailData
                    ? renderExitInfo()
                    : !detailsLoading && (
                        <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 border border-gray-200 dark:border-gray-600 text-center text-sm text-gray-400">
                            Loading exit details...
                        </div>
                    )
                }

                {/* Timeline */}
                {hasDetailData && renderTimeline()}

                {/* Approval history */}
                <RemarksHistory
                    isOpen={showRemarksHistory}
                    onToggle={() => setShowRemarksHistory(!showRemarksHistory)}
                    remarks={remarks}
                    loading={remarksLoading}
                    title="Approval History"
                />

                {/* Verification checkbox + comment */}
                <VerificationInput
                    isVerified={isVerified}
                    onVerifiedChange={setIsVerified}
                    comment={verificationComment}
                    onCommentChange={(e) => setVerificationComment(e.target.value)}
                    config={{
                        checkboxLabel: '✓ I have verified the exit details, dates, and supporting documents',
                        checkboxDescription: 'Including resignation date, relieving date, and employee information',
                        commentLabel: 'Verification Comments',
                        commentPlaceholder: 'Please verify the exit dates, employee details, and any discrepancies...',
                        commentRequired: true,
                        commentRows: 4,
                        commentMaxLength: 1000,
                        showCharCount: true,
                        validationStyle: 'dynamic',
                        checkboxGradient: 'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20',
                        commentGradient: 'from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20',
                        commentBorder: 'border-blue-200 dark:border-blue-700'
                    }}
                />

                {/* Action buttons */}
                {statusLoading ? (
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-center space-x-3">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                            <span className="text-gray-600 dark:text-gray-400">Loading actions...</span>
                        </div>
                    </div>
                ) : statusError ? (
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                        <p className="text-red-600 dark:text-red-400 text-center">⚠️ Error loading actions: {statusError}</p>
                    </div>
                ) : !hasActions || !enabledActions?.length ? (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6 border border-yellow-200 dark:border-yellow-700">
                        <p className="text-yellow-700 dark:text-yellow-400 text-center">
                            ℹ️ No actions available for this exit record
                        </p>
                    </div>
                ) : (
                    <ActionButtons
                        actions={enabledActions}
                        onActionClick={handleActionClick}
                        loading={approvalLoading}
                        isVerified={isVerified}
                        comment={verificationComment}
                        showValidation={true}
                        excludeActions={['send back']}
                    />
                )}
            </div>
        );
    };

    // ── Root render ────────────────────────────────────────────────────────────
    return (
        <div className="space-y-6">

            <InboxHeader
                title={`${InboxTitle || 'Employee Exit Verification'} (${exitGrid.length})`}
                subtitle={ModuleDisplayName}
                itemCount={exitGrid.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={LogOut}
                badgeText="Exit Verification"
                badgeCount={exitGrid.length}
                enableViewToggle
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by name, emp code, ref no...',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value)
                }}
                filters={[
                    {
                        value: filterStatus,
                        onChange: (e) => setFilterStatus(e.target.value),
                        defaultLabel: 'All Statuses',
                        options: uniqueStatuses
                    }
                ]}
            />

            <div className="px-6 -mt-auto mb-6">
                <StatsCards
                    cards={statsCards}
                    variant="simple"
                    gridCols="grid-cols-1 md:grid-cols-4"
                    gap="gap-4"
                />
            </div>

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredItems,
                    selectedItem: selectedItem,
                    onItemSelect: handleItemSelect,
                    renderItem: renderItemCard,
                    renderListItem: renderListItem,
                    renderCollapsedItem: renderCollapsedItem,
                    loading: inboxLoading,
                    error: inboxError,
                    onRefresh: handleRefresh,
                    config: {
                        title: 'Pending Verification',
                        icon: Clock,
                        emptyMessage: 'No exit records found!',
                        itemKey: 'Id',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20',
                    },
                    renderPopupContent: (_item) => renderDetailContent(),
                    popupConfig: {
                        title: 'Employee Exit Verification',
                        icon: LogOut,
                        headerGradient: 'from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem: selectedItem,
                    loading: false,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'Exit Details',
                        icon: LogOut,
                        selectedTitle: 'Exit Verification',
                        emptyTitle: 'No Exit Record Selected',
                        emptyMessage: 'Select an exit record from the list to view details and verify the employee exit.',
                        headerGradient: 'from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20',
                        maxHeight: 'calc(100vh - 200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />

            {showDocumentModal && d.DocBaseString && (
                <AttachmentModal
                    isOpen={showDocumentModal}
                    onClose={() => setShowDocumentModal(false)}
                    fileUrl={getDocumentUrl(d.DocBaseString, d.DocType)}
                    fileName={`ExitDocument_${d.Id || ''}.${(d.DocType || 'pdf').toLowerCase()}`}
                    title="Exit Supporting Document"
                    subtitle={`Employee: ${d.EmployeeName || d.EmpName || d.EmpRefNo || ''} | Type: ${d.DocType}`}
                    theme="indigo"
                    isImageFile={() => ['jpg', 'jpeg', 'png'].includes((d.DocType || '').toLowerCase())}
                    isPdfFile={() => (d.DocType || '').toLowerCase() === 'pdf'}
                    onError={() => toast.error('Failed to load document')}
                />
            )}
        </div>
    );
};

export default VerifyEmployeeExit;
